import { useEffect, useState } from 'react';
import { adminApi, type AdminAuditLog, type PaginatedResponse } from '../../lib/api';

export function AuditLogPage() {
  const [logs, setLogs] = useState<PaginatedResponse<AdminAuditLog> | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(1);
  const [actionFilter, setActionFilter] = useState('');
  const [actions, setActions] = useState<string[]>([]);

  useEffect(() => {
    adminApi.getAuditLogActions().then((data) => setActions(data.actions));
  }, []);

  useEffect(() => {
    const fetchLogs = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await adminApi.getAuditLogs({
          page,
          action: actionFilter || undefined,
        });
        setLogs(data);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to load audit logs');
      } finally {
        setIsLoading(false);
      }
    };

    fetchLogs();
  }, [page, actionFilter]);

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <h1 className="font-display text-2xl text-parchment">Audit Log</h1>

        {/* Action Filter */}
        <select
          value={actionFilter}
          onChange={(e) => {
            setActionFilter(e.target.value);
            setPage(1);
          }}
          className="px-3 py-2 bg-void border border-stone/30 rounded-lg text-parchment text-sm focus:outline-none focus:border-rune"
        >
          <option value="">All Actions</option>
          {actions.map((action) => (
            <option key={action} value={action}>
              {action}
            </option>
          ))}
        </select>
      </div>

      {/* Error State */}
      {error && (
        <div className="p-4 bg-ember/10 border border-ember/30 rounded-lg text-ember">{error}</div>
      )}

      {/* Loading State */}
      {isLoading && (
        <div className="text-center py-12">
          <div className="text-mist animate-pulse">Loading audit logs...</div>
        </div>
      )}

      {/* Logs Table */}
      {!isLoading && logs && (
        <>
          <div className="bg-night border border-stone/30 rounded-xl overflow-hidden">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-mist text-left border-b border-stone/30 bg-stone/20">
                  <th className="px-4 py-3 font-medium">Timestamp</th>
                  <th className="px-4 py-3 font-medium">Admin</th>
                  <th className="px-4 py-3 font-medium">Action</th>
                  <th className="px-4 py-3 font-medium">Target</th>
                  <th className="px-4 py-3 font-medium">Details</th>
                </tr>
              </thead>
              <tbody>
                {logs.data.map((log) => (
                  <tr key={log.id} className="border-b border-stone/20 last:border-0">
                    <td className="px-4 py-3 text-mist text-xs whitespace-nowrap">
                      {formatDate(log.createdAt)}
                    </td>
                    <td className="px-4 py-3 text-parchment text-xs">{log.adminUser.email}</td>
                    <td className="px-4 py-3">
                      <span className="px-2 py-0.5 bg-rune/20 text-rune rounded text-xs">
                        {log.action}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-mist text-xs">
                      <span className="text-parchment">{log.targetType}</span>
                      <span className="mx-1">/</span>
                      <span className="font-mono">{log.targetId}</span>
                    </td>
                    <td className="px-4 py-3">
                      {log.details ? (
                        <details className="cursor-pointer">
                          <summary className="text-xs text-mist hover:text-parchment">
                            View details
                          </summary>
                          <pre className="mt-2 p-2 bg-void rounded text-xs text-mist overflow-auto max-w-xs">
                            {JSON.stringify(log.details, null, 2)}
                          </pre>
                        </details>
                      ) : (
                        <span className="text-mist/50 text-xs">-</span>
                      )}
                    </td>
                  </tr>
                ))}
                {logs.data.length === 0 && (
                  <tr>
                    <td colSpan={5} className="px-4 py-8 text-center text-mist">
                      No audit logs found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {logs.pagination.totalPages > 1 && (
            <div className="flex items-center justify-between">
              <div className="text-sm text-mist">{logs.pagination.total} total entries</div>
              <div className="flex gap-2">
                <button
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                  className="px-3 py-1.5 text-sm bg-stone/30 text-mist rounded-lg hover:bg-stone/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Previous
                </button>
                <span className="px-3 py-1.5 text-sm text-mist">
                  Page {page} of {logs.pagination.totalPages}
                </span>
                <button
                  onClick={() => setPage(page + 1)}
                  disabled={page === logs.pagination.totalPages}
                  className="px-3 py-1.5 text-sm bg-stone/30 text-mist rounded-lg hover:bg-stone/50 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

function formatDate(isoString: string): string {
  const date = new Date(isoString);
  return date.toLocaleDateString() + ' ' + date.toLocaleTimeString();
}
